/* ═══════════════════════════════════════════════════════════════
   InvoicesPage — Real invoice management.

   Tracks invoices end-to-end short of money movement. Bookkeepers /
   PMs / admins draft them, send them, mark them paid, and watch the
   AR aging tighten or rot.  PDF export uses jspdf.

   Layout:
     - List view: filter pills (status), search, summary cards
     - Click any row → editor modal (or full-screen on small windows)
     - Editor: client + project picker, line items table (manual + "Pull
       billable hours from period"), totals, lifecycle dates, PDF download

   Permissions:
     - invoices.view_own | view_all to see the page
     - invoices.create / edit / send / mark_paid / void / delete
     - invoices.view_financials gates $ visibility (rare — most viewers
       can see invoice existence but not amounts)
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useMemo } from 'react';
import { SearchableSelect } from '../components/SearchableSelect';
import { Button } from '../components/ui/button';
import EmptyState from '../components/EmptyState';
import { Plus, X, Download } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { InvoiceService } from '../services/invoiceService';
import { ProjectService } from '../services/projectService';
import { OrgService } from '../services/orgService';
import { TeamService } from '../services/teamService';
import { TimeService } from '../services/timeService';
import { AdminService } from '../services/adminService';
import { hasPermission } from '../../shared/permissions';
import type {
  Invoice,
  InvoiceLineItem,
  Project,
  Organization,
  HourEntry,
  TeamMember,
  BillingRole,
} from '../../shared/types';
import type { AuthUser } from '../services/authService';

interface InvoicesPageProps {
  toast: (msg: string, isError?: boolean) => void;
  authUser?: AuthUser | null;
  /** Optional one-shot filter from a cross-page drill — e.g. clicking
   *  "AR Aging" on a project dashboard lands here filtered to that
   *  project (status='overdue') or with a specific invoice opened. */
  deepLink?: {
    view: 'invoices';
    projectId?: string;
    invoiceId?: string;
    status?: 'overdue' | 'all';
  } | null;
  onDeepLinkConsumed?: () => void;
}

// ─── Status palette ─────────────────────────────────────────────────

const STATUS_META: Record<string, { label: string; bg: string; color: string }> = {
  draft: { label: 'Draft', bg: 'var(--sa)', color: 'var(--tm)' },
  sent: {
    label: 'Sent',
    bg: 'color-mix(in srgb, var(--color-info) 12%, transparent)',
    color: 'var(--color-info)',
  },
  partial: {
    label: 'Partial',
    bg: 'color-mix(in srgb, var(--color-warning) 14%, transparent)',
    color: 'var(--color-warning)',
  },
  paid: {
    label: 'Paid',
    bg: 'color-mix(in srgb, var(--ok) 12%, transparent)',
    color: 'var(--ok)',
  },
  void: {
    label: 'Void',
    bg: 'color-mix(in srgb, var(--dg) 10%, transparent)',
    color: 'var(--dg)',
  },
};

const fmtMoney = (n: number) =>
  '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

// ─── Page ────────────────────────────────────────────────────────────

export default function InvoicesPage({
  toast,
  authUser,
  deepLink,
  onDeepLinkConsumed,
}: InvoicesPageProps) {
  const perms = authUser?.permissions || [];
  const canViewAll = hasPermission(perms, 'invoices.view_all');
  const canViewMoney = hasPermission(perms, 'invoices.view_financials');
  const canCreate = hasPermission(perms, 'invoices.create');
  const canEdit = hasPermission(perms, 'invoices.edit');
  const canSend = hasPermission(perms, 'invoices.send');
  const canMarkPaid = hasPermission(perms, 'invoices.mark_paid');
  const canVoid = hasPermission(perms, 'invoices.void');
  const canDelete = hasPermission(perms, 'invoices.delete');
  const canExportPdf = hasPermission(perms, 'invoices.export_pdf');

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [billingRoles, setBillingRoles] = useState<BillingRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Invoice | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Optional project scope set by deep-link drill (sticky until cleared by
  // the user or a different deep-link). Visible as a chip in the toolbar.
  const [projectFilterId, setProjectFilterId] = useState<string | null>(null);
  // Pending invoiceId to open once data has loaded (deep-link race fix).
  const [pendingOpenInvoiceId, setPendingOpenInvoiceId] = useState<string | null>(null);

  // Consume deep-link once. Note we run on every deepLink change, not
  // every render, so a fresh drill from the dashboard re-applies even
  // if the user already navigated within the page.
  useEffect(() => {
    if (!deepLink) return;
    if (deepLink.projectId) setProjectFilterId(deepLink.projectId);
    if (deepLink.status === 'overdue') {
      setStatusFilter('sent');
    } else if (deepLink.status === 'all') {
      setStatusFilter('all');
    }
    if (deepLink.invoiceId) setPendingOpenInvoiceId(deepLink.invoiceId);
    onDeepLinkConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const [invR, pR, oR, mR, brR] = await Promise.allSettled([
        InvoiceService.list(),
        ProjectService.list(),
        OrgService.list(),
        TeamService.list(),
        AdminService.billingRoles.list().catch(() => [] as BillingRole[]),
      ]);
      if (invR.status === 'fulfilled') setInvoices(invR.value);
      if (pR.status === 'fulfilled') setProjects(pR.value);
      if (oR.status === 'fulfilled') setOrgs(oR.value);
      if (mR.status === 'fulfilled') setMembers(mR.value);
      if (brR.status === 'fulfilled') setBillingRoles(brR.value);
      const rejected = [invR, pR, oR, mR, brR].filter(
        (r): r is PromiseRejectedResult => r.status === 'rejected'
      );
      if (rejected.length > 0) {
        console.error('[InvoicesPage] partial load failure:', rejected);
        const msg = 'Some invoice data failed to load. Check your connection and try again.';
        setLoadError(msg);
        toast(msg, true);
      }
    } catch (err: any) {
      console.error('[InvoicesPage] load failed:', err);
      const msg = 'Load failed: ' + (err?.message || String(err));
      setLoadError(msg);
      toast(msg, true);
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('specforma-sync-complete', h);
    return () => window.removeEventListener('specforma-sync-complete', h);
  }, []);

  const projectName = (id: string) => projects.find((p) => p.id === id)?.name || 'Unknown';
  const projectNumber = (id: string) => projects.find((p) => p.id === id)?.projectNumber || '';
  const orgName = (id: string) => orgs.find((o) => o.id === id)?.name || 'Unknown';

  // Visible invoices — apply view scope + status filter + search
  const visibleInvoices = useMemo(() => {
    let list = invoices.filter((i) => !i.archivedAt);
    if (!canViewAll) {
      // Limited to projects the user is on — best-effort by checking if the
      // user's id matches projects.pic / projects.pm. Server-side enforcement
      // is the source of truth.
      const myProjectIds = new Set(
        projects
          .filter((p: any) => p.pic === authUser?.teamMemberId || p.pm === authUser?.teamMemberId)
          .map((p) => p.id)
      );
      list = list.filter((i) => myProjectIds.has(i.projectId));
    }
    if (projectFilterId) list = list.filter((i) => i.projectId === projectFilterId);
    if (statusFilter !== 'all') list = list.filter((i) => i.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (i) =>
          (i.invoiceNumber || '').toLowerCase().includes(q) ||
          orgName(i.organizationId).toLowerCase().includes(q) ||
          projectName(i.projectId).toLowerCase().includes(q) ||
          projectNumber(i.projectId).toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) =>
      (b.issueDate || b.date || '').localeCompare(a.issueDate || a.date || '')
    );
  }, [invoices, projects, statusFilter, searchQuery, canViewAll, authUser, projectFilterId]);

  // Once invoices are loaded, satisfy a pending deep-link "open invoice X".
  useEffect(() => {
    if (!pendingOpenInvoiceId || invoices.length === 0) return;
    const target = invoices.find((i) => i.id === pendingOpenInvoiceId);
    if (target) setEditing(target);
    setPendingOpenInvoiceId(null);
  }, [pendingOpenInvoiceId, invoices]);

  // Summary cards
  const stats = useMemo(() => {
    const buckets = InvoiceService.ageingBuckets(invoices);
    const totalOpen = buckets.current + buckets.d30 + buckets.d60 + buckets.d90;
    const draftCount = invoices.filter((i) => i.status === 'draft').length;
    const sentCount = invoices.filter((i) => i.status === 'sent' || i.status === 'partial').length;
    const paidThisMonth = invoices
      .filter((i) => {
        if (i.status !== 'paid') return false;
        const paid = i.paidDate || '';
        return paid.slice(0, 7) === todayIso().slice(0, 7);
      })
      .reduce((s, i) => s + (i.total || 0), 0);
    return { totalOpen, buckets, draftCount, sentCount, paidThisMonth };
  }, [invoices]);

  async function createNew() {
    if (!canCreate) return;
    const nextNum = await InvoiceService.nextInvoiceNumber();
    const today = todayIso();
    const draft: Invoice = {
      id: '',
      projectId: '',
      organizationId: '',
      invoiceNumber: nextNum,
      date: today,
      issueDate: today,
      dueDate: addDays(today, 30),
      lineItems: [],
      subtotal: 0,
      tax: 0,
      taxRate: 0,
      total: 0,
      amountPaid: 0,
      status: 'draft',
      terms: 'Net 30',
      notes: '',
      createdBy: authUser?.teamMemberId,
      createdAt: '',
      updatedAt: '',
    };
    setEditing(draft);
  }

  // ─── Render ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ height: '100%', overflow: 'hidden', padding: 24 }}>
        <InvoicesSkeleton />
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: 24,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 16,
        }}
      >
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Invoices</h2>
          <p style={{ fontSize: 13, color: 'var(--tm)', margin: '4px 0 0' }}>
            Track invoices, sent dates, and payments. SpecForma is tracking-only — record actual
            payments here and reconcile against your accounting system.
          </p>
        </div>
        {canCreate && (
          <Button onClick={createNew}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            New invoice
          </Button>
        )}
      </div>

      {/* Load error */}
      {loadError && (
        <div
          role="alert"
          style={{
            padding: '10px 14px',
            marginBottom: 16,
            borderRadius: 6,
            background: 'color-mix(in srgb, var(--dg) 10%, transparent)',
            border: '1px solid color-mix(in srgb, var(--dg) 30%, transparent)',
            color: 'var(--dg)',
            fontSize: 13,
          }}
        >
          {loadError}
        </div>
      )}

      {/* Summary cards */}
      {canViewMoney && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 12,
            marginBottom: 20,
          }}
        >
          <SummaryCard
            label="OUTSTANDING"
            value={fmtMoney(stats.totalOpen)}
            color="var(--color-info)"
          />
          <SummaryCard
            label="OVER 60 DAYS"
            value={fmtMoney(stats.buckets.d60 + stats.buckets.d90)}
            color={stats.buckets.d60 + stats.buckets.d90 > 0 ? 'var(--dg)' : 'var(--tm)'}
          />
          <SummaryCard
            label="PAID THIS MONTH"
            value={fmtMoney(stats.paidThisMonth)}
            color="var(--ok)"
          />
          <SummaryCard label="DRAFTS" value={String(stats.draftCount)} color="var(--tm)" />
          <SummaryCard
            label="SENT / PARTIAL"
            value={String(stats.sentCount)}
            color="var(--color-info)"
          />
        </div>
      )}

      {/* Filter + search */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {projectFilterId && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '5px 10px',
              borderRadius: 14,
              background: 'color-mix(in srgb, var(--color-info) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--color-info) 40%, transparent)',
              color: 'var(--color-info)',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            <span>Project: {projectName(projectFilterId)}</span>
            <button
              onClick={() => setProjectFilterId(null)}
              title="Clear project filter"
              aria-label="Clear project filter"
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                border: 'none',
                background: 'color-mix(in srgb, var(--color-info) 20%, transparent)',
                color: 'var(--color-info)',
                cursor: 'pointer',
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={12} aria-hidden="true" />
            </button>
          </div>
        )}
        <div
          style={{ display: 'flex', gap: 4, background: 'var(--sa)', borderRadius: 6, padding: 2 }}
        >
          {(['all', 'draft', 'sent', 'partial', 'paid', 'void'] as const).map((s) => {
            const meta = s === 'all' ? { label: 'All', color: 'var(--tx)' } : STATUS_META[s];
            const active = statusFilter === s;
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                style={{
                  padding: '5px 12px',
                  borderRadius: 4,
                  background: active ? 'var(--sf)' : 'transparent',
                  color: active ? meta.color : 'var(--tm)',
                  border: 'none',
                  fontSize: 11,
                  fontWeight: active ? 700 : 500,
                  cursor: 'pointer',
                  boxShadow: active ? '0 1px 3px rgba(0,0,0,.08)' : 'none',
                }}
              >
                {meta.label}
              </button>
            );
          })}
        </div>
        <div style={{ flex: 1 }} />
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search invoice #, client, or project..."
          aria-label="Search invoices"
          style={{
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid var(--bd)',
            background: 'var(--ib)',
            color: 'var(--tx)',
            fontSize: 13,
            width: 280,
          }}
        />
      </div>

      {/* List */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          borderRadius: 8,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '120px 1fr 1.5fr 100px 100px 100px 100px 80px',
            background: 'var(--sa)',
            padding: '8px 14px',
            borderBottom: '1px solid var(--bd)',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--tm)',
            textTransform: 'uppercase',
            letterSpacing: '.04em',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <span>Invoice #</span>
          <span>Client</span>
          <span>Project</span>
          <span>Issued</span>
          <span>Due</span>
          <span style={{ textAlign: 'right' }}>{canViewMoney ? 'Total' : 'Status'}</span>
          <span>Status</span>
          <span></span>
        </div>

        {visibleInvoices.map((inv) => {
          const meta = STATUS_META[inv.status] || STATUS_META.draft;
          return (
            <div
              key={inv.id}
              onClick={() => canEdit && setEditing(inv)}
              style={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr 1.5fr 100px 100px 100px 100px 80px',
                padding: '10px 14px',
                borderBottom: '1px solid var(--bd)',
                cursor: canEdit ? 'pointer' : 'default',
                transition: 'background .1s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '')}
            >
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 13,
                  color: 'var(--tx)',
                }}
              >
                {inv.invoiceNumber}
              </span>
              <span
                style={{
                  fontSize: 13,
                  color: 'var(--tx)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {orgName(inv.organizationId)}
              </span>
              <span
                style={{
                  fontSize: 13,
                  color: 'var(--tx)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {projectNumber(inv.projectId) && (
                  <span style={{ color: 'var(--tm)', marginRight: 6 }}>
                    {projectNumber(inv.projectId)}
                  </span>
                )}
                {projectName(inv.projectId)}
              </span>
              <span
                style={{
                  fontSize: 13,
                  color: 'var(--tm)',
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {inv.issueDate || inv.date}
              </span>
              <span
                style={{
                  fontSize: 13,
                  color:
                    inv.dueDate &&
                    inv.dueDate < todayIso() &&
                    inv.status !== 'paid' &&
                    inv.status !== 'void'
                      ? 'var(--dg)'
                      : 'var(--tm)',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontWeight:
                    inv.dueDate && inv.dueDate < todayIso() && inv.status !== 'paid' ? 700 : 500,
                }}
              >
                {inv.dueDate || '—'}
              </span>
              <span
                style={{
                  textAlign: 'right',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--tx)',
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {canViewMoney ? fmtMoney(inv.total || 0) : '—'}
              </span>
              <span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '3px 10px',
                    borderRadius: 999,
                    background: meta.bg,
                    color: meta.color,
                    textTransform: 'uppercase',
                    letterSpacing: '.05em',
                  }}
                >
                  {meta.label}
                </span>
              </span>
              <span style={{ textAlign: 'right' }}>
                {canExportPdf && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      generatePdf(inv, projects, orgs);
                    }}
                    title="Download PDF"
                    aria-label="Download PDF"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--tm)',
                      padding: 8,
                      borderRadius: 6,
                    }}
                  >
                    <Download size={16} aria-hidden="true" />
                  </button>
                )}
              </span>
            </div>
          );
        })}

        {visibleInvoices.length === 0 && (
          <EmptyState
            title={
              invoices.length === 0 ? 'No invoices yet' : 'No invoices match the current filters'
            }
            subtitle={
              invoices.length === 0
                ? 'Click New invoice to create your first.'
                : 'Try a different status, search term, or clear the project filter.'
            }
            action={
              invoices.length === 0 && canCreate
                ? { label: 'New invoice', onClick: createNew }
                : undefined
            }
          />
        )}
      </div>

      {/* Editor */}
      {editing && (
        <InvoiceEditor
          invoice={editing}
          projects={projects}
          orgs={orgs}
          members={members}
          billingRoles={billingRoles}
          canEdit={canEdit}
          canSend={canSend}
          canMarkPaid={canMarkPaid}
          canVoid={canVoid}
          canDelete={canDelete}
          canViewMoney={canViewMoney}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
            toast('Invoice saved');
          }}
          onDeleted={async () => {
            setEditing(null);
            await load();
            toast('Invoice deleted');
          }}
          toast={toast}
        />
      )}
    </div>
  );
}

// ─── Loading skeleton ────────────────────────────────────────────────

/** Shown during the first data load (skeletons over spinners — DESIGN.md §4). */
function InvoicesSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading invoices"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 16 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          style={{
            height: 24,
            width: 160,
            background: 'var(--sa)',
            borderRadius: 'var(--radius-md)',
            animation: 'pulse 1.4s ease-in-out infinite',
          }}
        />
        <div
          style={{
            height: 36,
            width: 130,
            background: 'var(--sa)',
            borderRadius: 'var(--radius-md)',
            animation: 'pulse 1.4s ease-in-out infinite',
          }}
        />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
        }}
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            style={{
              height: 66,
              background: 'var(--sa)',
              borderRadius: 'var(--radius-lg)',
              animation: 'pulse 1.4s ease-in-out infinite',
            }}
          />
        ))}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            style={{
              height: 38,
              background: 'var(--sa)',
              borderRadius: 'var(--radius-md)',
              opacity: Math.max(0.25, 0.95 - i * 0.08),
              animation: 'pulse 1.4s ease-in-out infinite',
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Summary card ────────────────────────────────────────────────────

function SummaryCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div
      style={{
        background: 'var(--sf)',
        borderRadius: 10,
        border: '1px solid var(--bd)',
        padding: '14px 16px',
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: 'var(--tm)',
          fontWeight: 700,
          marginBottom: 6,
          letterSpacing: '.05em',
        }}
      >
        {label}
      </div>
      <div
        style={{ fontSize: 22, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color }}
      >
        {value}
      </div>
    </div>
  );
}

// ─── Editor modal ────────────────────────────────────────────────────

function InvoiceEditor({
  invoice,
  projects,
  orgs,
  members,
  billingRoles,
  canEdit,
  canSend,
  canMarkPaid,
  canVoid,
  canDelete,
  canViewMoney,
  onClose,
  onSaved,
  onDeleted,
  toast,
}: {
  invoice: Invoice;
  projects: Project[];
  orgs: Organization[];
  members: TeamMember[];
  billingRoles: BillingRole[];
  canEdit: boolean;
  canSend: boolean;
  canMarkPaid: boolean;
  canVoid: boolean;
  canDelete: boolean;
  canViewMoney: boolean;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
  toast: (msg: string, isError?: boolean) => void;
}) {
  const [draft, setDraft] = useState<Invoice>(invoice);
  const [saving, setSaving] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pullStart, setPullStart] = useState(addDays(todayIso(), -30));
  const [pullEnd, setPullEnd] = useState(todayIso());
  const [paymentAmount, setPaymentAmount] = useState('');

  // Auto-recompute totals on line item changes.
  useEffect(() => {
    const recomputed = InvoiceService.recompute(draft);
    if (
      recomputed.subtotal !== draft.subtotal ||
      recomputed.tax !== draft.tax ||
      recomputed.total !== draft.total
    ) {
      setDraft(recomputed);
    }
    // Intentionally only watch lineItems + taxRate
  }, [draft.lineItems, draft.taxRate]);

  function update(patch: Partial<Invoice>) {
    setDraft((d) => ({ ...d, ...patch }));
  }

  function updateLineItem(idx: number, patch: Partial<InvoiceLineItem>) {
    setDraft((d) => {
      const next = [...d.lineItems];
      const item = { ...next[idx], ...patch };
      // Auto-recalc amount when hours or rate changes
      const hours = item.hours ?? item.quantity ?? 0;
      const rate = item.rate ?? 0;
      item.amount = Math.round(hours * rate * 100) / 100;
      item.quantity = hours;
      next[idx] = item;
      return { ...d, lineItems: next };
    });
  }

  function addManualLine() {
    setDraft((d) => ({
      ...d,
      lineItems: [
        ...d.lineItems,
        {
          description: '',
          hours: 0,
          quantity: 0,
          rate: 0,
          amount: 0,
          sourceKind: 'manual' as const,
        },
      ],
    }));
  }

  function removeLine(idx: number) {
    setDraft((d) => ({ ...d, lineItems: d.lineItems.filter((_, i) => i !== idx) }));
  }

  async function pullBillableHours() {
    if (!draft.projectId) {
      toast('Pick a project first', true);
      return;
    }
    setPulling(true);
    try {
      const entries = await TimeService.listEntries(draft.projectId, pullStart, pullEnd);
      // Skip entries already invoiced (have invoice_id) or non-billable
      const billable = entries.filter(
        (e) => (e as any).billable !== false && !(e as any).invoiceId
      );
      if (billable.length === 0) {
        toast('No new billable hours in that range', true);
        return;
      }
      const rateFor = (e: HourEntry) => {
        const m = members.find((mm) => mm.id === e.teamMemberId);
        if (!m) return 0;
        const role = billingRoles.find((r) => r.id === m.billingRoleId);
        return m.hourlyRate ?? role?.defaultRate ?? 0;
      };
      const descFor = (e: HourEntry) => {
        const m = members.find((mm) => mm.id === e.teamMemberId);
        const role = billingRoles.find((r) => r.id === m?.billingRoleId);
        return role ? role.name : m?.name || 'Time';
      };
      const newLines = InvoiceService.lineItemsFromTimeEntries(billable, rateFor, descFor);
      setDraft((d) => ({
        ...d,
        lineItems: [...d.lineItems, ...newLines],
      }));
      toast(`Added ${newLines.length} line items from ${billable.length} hour entries`);
    } catch (err: any) {
      toast('Pull failed: ' + (err?.message || String(err)), true);
    } finally {
      setPulling(false);
    }
  }

  async function save() {
    if (!draft.projectId || !draft.organizationId) {
      toast('Pick a client and project first', true);
      return;
    }
    setSaving(true);
    try {
      await InvoiceService.save(draft);
      onSaved();
    } catch (err: any) {
      toast('Save failed: ' + (err?.message || String(err)), true);
    } finally {
      setSaving(false);
    }
  }

  async function markSent() {
    if (!canSend) return;
    const next: Invoice = { ...draft, status: 'sent', sentDate: todayIso() };
    setDraft(next);
    setSaving(true);
    try {
      await InvoiceService.save(next);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  async function recordPayment() {
    if (!canMarkPaid) return;
    const amt = parseFloat(paymentAmount);
    if (!isFinite(amt) || amt <= 0) {
      toast('Enter a payment amount', true);
      return;
    }
    const newPaid = (draft.amountPaid || 0) + amt;
    const isFull = newPaid >= (draft.total || 0);
    const next: Invoice = {
      ...draft,
      amountPaid: Math.min(newPaid, draft.total || 0),
      status: isFull ? 'paid' : 'partial',
      paidDate: isFull ? todayIso() : draft.paidDate,
    };
    setDraft(next);
    setPaymentAmount('');
    setSaving(true);
    try {
      await InvoiceService.save(next);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  async function voidInvoice() {
    if (!canVoid) return;
    if (!confirm('Void this invoice? It will remain visible but excluded from AR.')) return;
    const next: Invoice = { ...draft, status: 'void' };
    setSaving(true);
    try {
      await InvoiceService.save(next);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  async function deleteInvoice() {
    if (!canDelete || !draft.id) return;
    if (!confirm('DELETE this invoice permanently? Use Void to keep an audit trail instead.'))
      return;
    setSaving(true);
    try {
      await InvoiceService.delete(draft.id);
      onDeleted();
    } finally {
      setSaving(false);
    }
  }

  const meta = STATUS_META[draft.status] || STATUS_META.draft;
  const orgName = (id: string) => orgs.find((o) => o.id === id)?.name || '';
  const projectsForOrg = draft.organizationId
    ? projects.filter(
        (p) =>
          (p as any).organizationId === draft.organizationId ||
          (p as any).clientOrgId === draft.organizationId
      )
    : projects;

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--bd)',
          borderRadius: 12,
          width: 'min(900px, 95vw)',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid var(--bd)',
            gap: 12,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
              {draft.id ? 'Edit Invoice' : 'New Invoice'}
            </h3>
            <div
              style={{
                fontSize: 13,
                color: 'var(--tm)',
                marginTop: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {draft.invoiceNumber}
              </span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: meta.bg,
                  color: meta.color,
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                {meta.label}
              </span>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {/* Top fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px 140px', gap: 12 }}>
            <Field label="Client">
              <SearchableSelect
                value={draft.organizationId}
                options={orgs.map((o) => ({ value: o.id, label: o.name }))}
                onChange={(v) => update({ organizationId: v })}
                disabled={!canEdit}
                placeholder="-- Pick client --"
                searchPlaceholder="Search clients…"
                style={inputStyle}
              />
            </Field>
            <Field label="Project">
              <SearchableSelect
                value={draft.projectId}
                options={projectsForOrg.map((p) => ({
                  value: p.id,
                  label: p.name,
                  hint: p.projectNumber || undefined,
                }))}
                onChange={(v) => update({ projectId: v })}
                disabled={!canEdit}
                placeholder="-- Pick project --"
                searchPlaceholder="Search projects…"
                style={inputStyle}
              />
            </Field>
            <Field label="Issue Date">
              <input
                type="date"
                value={draft.issueDate || draft.date || ''}
                onChange={(e) => update({ issueDate: e.target.value, date: e.target.value })}
                disabled={!canEdit}
                style={inputStyle}
              />
            </Field>
            <Field label="Due Date">
              <input
                type="date"
                value={draft.dueDate || ''}
                onChange={(e) => update({ dueDate: e.target.value })}
                disabled={!canEdit}
                style={inputStyle}
              />
            </Field>
          </div>

          {/* Pull from time + add manual */}
          {canEdit && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                padding: '10px 12px',
                background: 'var(--sa)',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              <span style={{ color: 'var(--tm)', fontWeight: 600 }}>Pull billable hours:</span>
              <input
                type="date"
                value={pullStart}
                onChange={(e) => setPullStart(e.target.value)}
                aria-label="Pull start date"
                style={{ ...inputStyle, padding: '4px 8px', width: 130, fontSize: 11 }}
              />
              <span style={{ color: 'var(--tm)' }}>to</span>
              <input
                type="date"
                value={pullEnd}
                onChange={(e) => setPullEnd(e.target.value)}
                aria-label="Pull end date"
                style={{ ...inputStyle, padding: '4px 8px', width: 130, fontSize: 11 }}
              />
              <button
                onClick={pullBillableHours}
                disabled={pulling || !draft.projectId}
                style={{
                  padding: '5px 12px',
                  borderRadius: 5,
                  border: '1px solid var(--ac)',
                  background: 'transparent',
                  color: 'var(--ac)',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: pulling ? 'wait' : 'pointer',
                  opacity: !draft.projectId ? 0.4 : 1,
                }}
              >
                {pulling ? 'Pulling…' : 'Pull'}
              </button>
              <span style={{ flex: 1 }} />
              <button
                onClick={addManualLine}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '5px 12px',
                  borderRadius: 5,
                  border: '1px dashed var(--bd)',
                  background: 'transparent',
                  color: 'var(--tx)',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <Plus size={12} aria-hidden="true" />
                Add manual line
              </button>
            </div>
          )}

          {/* Line items */}
          <div style={{ border: '1px solid var(--bd)', borderRadius: 6, overflow: 'hidden' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 80px 100px 110px 30px',
                gap: 8,
                padding: '8px 12px',
                background: 'var(--sa)',
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--tm)',
                textTransform: 'uppercase',
                letterSpacing: '.05em',
              }}
            >
              <span>Description</span>
              <span style={{ textAlign: 'right' }}>Hours</span>
              <span style={{ textAlign: 'right' }}>Rate</span>
              <span style={{ textAlign: 'right' }}>Amount</span>
              <span></span>
            </div>
            {draft.lineItems.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--tm)', fontSize: 13 }}>
                No line items yet. Pull billable hours from time tracking, or add manual lines.
              </div>
            )}
            {draft.lineItems.map((li, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 80px 100px 110px 30px',
                  gap: 8,
                  padding: '6px 12px',
                  borderTop: '1px solid var(--bd)',
                  alignItems: 'center',
                }}
              >
                <input
                  value={li.description}
                  onChange={(e) => updateLineItem(idx, { description: e.target.value })}
                  disabled={!canEdit}
                  placeholder="e.g. Engineering time, Sept 2026"
                  aria-label={`Description for line ${idx + 1}`}
                  style={{ ...inputStyle, padding: '5px 8px' }}
                />
                <input
                  type="number"
                  step="0.25"
                  value={li.hours || 0}
                  onChange={(e) => updateLineItem(idx, { hours: parseFloat(e.target.value) || 0 })}
                  disabled={!canEdit}
                  aria-label={`Hours for line ${idx + 1}`}
                  style={{
                    ...inputStyle,
                    padding: '5px 8px',
                    textAlign: 'right',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                />
                <input
                  type="number"
                  step="0.01"
                  value={li.rate || 0}
                  onChange={(e) => updateLineItem(idx, { rate: parseFloat(e.target.value) || 0 })}
                  disabled={!canEdit}
                  aria-label={`Rate for line ${idx + 1}`}
                  style={{
                    ...inputStyle,
                    padding: '5px 8px',
                    textAlign: 'right',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                />
                <span
                  style={{
                    textAlign: 'right',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--tx)',
                  }}
                >
                  {fmtMoney(li.amount || 0)}
                </span>
                {canEdit && (
                  <button
                    onClick={() => removeLine(idx)}
                    aria-label={`Remove line ${idx + 1}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--dg)',
                      cursor: 'pointer',
                      padding: 5,
                      borderRadius: 4,
                    }}
                    title="Remove line"
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Totals */}
          {canViewMoney && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ minWidth: 280, fontSize: 13 }}>
                <Row label="Subtotal" value={fmtMoney(draft.subtotal || 0)} />
                <Row
                  label={
                    <span>
                      Tax (
                      <input
                        type="number"
                        step="0.1"
                        value={draft.taxRate || 0}
                        onChange={(e) => update({ taxRate: parseFloat(e.target.value) || 0 })}
                        disabled={!canEdit}
                        aria-label="Tax rate percent"
                        style={{
                          width: 50,
                          padding: '2px 4px',
                          fontSize: 11,
                          background: 'var(--ib)',
                          border: '1px solid var(--bd)',
                          borderRadius: 3,
                          color: 'var(--tx)',
                        }}
                      />
                      %)
                    </span>
                  }
                  value={fmtMoney(draft.tax || 0)}
                />
                <Row label="Total" value={fmtMoney(draft.total || 0)} bold />
                {(draft.amountPaid || 0) > 0 && (
                  <>
                    <Row label="Paid" value={fmtMoney(draft.amountPaid || 0)} color="var(--ok)" />
                    <Row
                      label="Balance"
                      value={fmtMoney((draft.total || 0) - (draft.amountPaid || 0))}
                      bold
                    />
                  </>
                )}
              </div>
            </div>
          )}

          {/* Notes + Terms */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Notes">
              <textarea
                value={draft.notes || ''}
                onChange={(e) => update({ notes: e.target.value })}
                disabled={!canEdit}
                placeholder="Internal notes or invoice memo"
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </Field>
            <Field label="Terms">
              <textarea
                value={draft.terms || ''}
                onChange={(e) => update({ terms: e.target.value })}
                disabled={!canEdit}
                placeholder="e.g. Net 30, payable to..."
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </Field>
          </div>

          {/* Payment recording (only when sent/partial) */}
          {(draft.status === 'sent' || draft.status === 'partial') && canMarkPaid && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                padding: '10px 12px',
                background: 'color-mix(in srgb, var(--ok) 6%, transparent)',
                border: '1px solid color-mix(in srgb, var(--ok) 30%, transparent)',
                borderRadius: 6,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ok)' }}>
                Record payment:
              </span>
              <input
                type="number"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder={`Up to ${fmtMoney((draft.total || 0) - (draft.amountPaid || 0))}`}
                aria-label="Payment amount"
                style={{
                  ...inputStyle,
                  padding: '5px 10px',
                  width: 180,
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              />
              <button
                onClick={recordPayment}
                style={{
                  padding: '5px 14px',
                  borderRadius: 5,
                  border: 'none',
                  background: 'var(--ok)',
                  color: 'var(--color-text-inverse)',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Record
              </button>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 20px',
            borderTop: '1px solid var(--bd)',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', gap: 8 }}>
            {canDelete && draft.id && (
              <Button variant="destructive" size="sm" onClick={deleteInvoice} disabled={saving}>
                Delete
              </Button>
            )}
            {canVoid && draft.status !== 'void' && draft.id && (
              <Button variant="outline" size="sm" onClick={voidInvoice} disabled={saving}>
                Void
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="outline" size="sm" onClick={() => generatePdf(draft, projects, orgs)}>
              Export PDF
            </Button>
            {draft.status === 'draft' && canSend && draft.id && (
              <Button variant="secondary" size="sm" onClick={markSent} disabled={saving}>
                Mark Sent
              </Button>
            )}
            {canEdit && (
              <Button
                size="sm"
                onClick={save}
                disabled={saving || !draft.projectId || !draft.organizationId}
              >
                {saving ? 'Saving…' : draft.id ? 'Save' : 'Create Invoice'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 4 }}>
        {label}
      </div>
      {children}
    </label>
  );
}

function Row({
  label,
  value,
  bold,
  color,
}: {
  label: React.ReactNode;
  value: string;
  bold?: boolean;
  color?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 0',
        borderTop: bold ? '1px solid var(--bd)' : 'none',
        marginTop: bold ? 6 : 0,
        paddingTop: bold ? 8 : 4,
        color: color || 'var(--tx)',
        fontWeight: bold ? 700 : 400,
        fontSize: bold ? 14 : 13,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{label}</span>
      <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
    </div>
  );
}

// ─── PDF generator ───────────────────────────────────────────────────
// NOTE: colors/sizes here are jsPDF document styling (points, RGB triples for
// a rendered PDF), not app CSS — the design-token system does not apply to
// generated-document content. Left as-is intentionally.

function generatePdf(invoice: Invoice, projects: Project[], orgs: Organization[]) {
  const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
  const project = projects.find((p) => p.id === invoice.projectId);
  const org = orgs.find((o) => o.id === invoice.organizationId);

  pdf.setFontSize(20);
  pdf.text('INVOICE', 40, 60);

  pdf.setFontSize(10);
  pdf.setTextColor(100);
  pdf.text(`Invoice #: ${invoice.invoiceNumber}`, 40, 90);
  pdf.text(`Issue Date: ${invoice.issueDate || invoice.date || ''}`, 40, 105);
  if (invoice.dueDate) pdf.text(`Due Date: ${invoice.dueDate}`, 40, 120);

  // Client block
  pdf.setTextColor(40);
  pdf.setFontSize(11);
  pdf.text('Bill To:', 350, 90);
  pdf.setFontSize(12);
  pdf.text(org?.name || 'Unknown Client', 350, 108);
  if (project) {
    pdf.setFontSize(10);
    pdf.setTextColor(100);
    pdf.text(
      `Project: ${project.projectNumber ? `${project.projectNumber} — ` : ''}${project.name}`,
      350,
      124
    );
  }

  // Line items
  autoTable(pdf, {
    startY: 160,
    head: [['Description', 'Hours', 'Rate', 'Amount']],
    body: invoice.lineItems.map((li) => [
      li.description,
      String(li.hours ?? li.quantity ?? 0),
      `$${(li.rate || 0).toFixed(2)}`,
      `$${(li.amount || 0).toFixed(2)}`,
    ]),
    headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
    columnStyles: {
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right' },
    },
    styles: { fontSize: 10, cellPadding: 6 },
  });

  // Totals
  // @ts-expect-error - autoTable adds finalY to pdf instance
  const totalsY = pdf.lastAutoTable.finalY + 20;
  pdf.setFontSize(10);
  pdf.text(`Subtotal: $${(invoice.subtotal || 0).toFixed(2)}`, 420, totalsY);
  if ((invoice.tax || 0) > 0) {
    pdf.text(
      `Tax (${invoice.taxRate || 0}%): $${(invoice.tax || 0).toFixed(2)}`,
      420,
      totalsY + 16
    );
  }
  pdf.setFontSize(13);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`TOTAL: $${(invoice.total || 0).toFixed(2)}`, 420, totalsY + (invoice.tax ? 38 : 22));

  if (invoice.terms) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(120);
    pdf.text('Terms:', 40, totalsY + 60);
    const termsLines = pdf.splitTextToSize(invoice.terms, 450);
    pdf.text(termsLines, 40, totalsY + 75);
  }

  pdf.save(`${invoice.invoiceNumber || 'invoice'}.pdf`);
}

// ─── Style constants ─────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  padding: '7px 10px',
  borderRadius: 5,
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 13,
  width: '100%',
};
